import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FileText, Folder, Minus, Plus, Search } from "react-feather";
import { spacing } from "../../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../../design-system/tokens/typography";
import { rawColors } from "../../../../design-system/tokens/colors";
import { borderRadius } from "../../../../design-system/tokens/borders";
import { Checkbox, Spinner } from "../../../atoms";
import { EmptyState } from "../../../molecules";
import {
  connectorAssetsApi,
  type ConnectorAssetResponsePayload,
} from "../../../../services/api";
import type { StepRenderProps } from "../ConnectorWizardModal.types";
import {
  mapBiAssetsFromTreeResponse,
  type BiDatasetTreeNode,
} from "./selectTreeMapper";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  width: 100%;
  min-height: 460px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
`;

const Title = styled.h3`
  margin: 0;
  ${typographyMixin("heading", "h3")}
  color: ${rawColors.text.highEmphasis};
`;

const SearchWrap = styled.label`
  width: 360px;
  max-width: 100%;
  height: 38px;
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  background: ${rawColors.structural.white};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: 0 ${spacing[3]};
  color: ${rawColors.text.lowEmphasis};
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  background: transparent;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};
`;

const InfoBanner = styled.div`
  border-radius: ${borderRadius.md};
  background-color: ${rawColors.primary[50]};
  color: ${rawColors.text.mediumEmphasis};
  ${typographyMixin("body", 4)}
  padding: ${spacing[2]} ${spacing[3]};
`;

const TreeCard = styled.div`
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.xl};
  background-color: ${rawColors.structural.white};
  padding: ${spacing[2]} 0;
  min-height: 260px;
  height: 520px;
`;

const TreeScroll = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Row = styled.div<{
  $depth: number;
  $active: boolean;
  $isLast: boolean;
}>`
  position: relative;
  display: grid;
  grid-template-columns: 14px 16px 14px 1fr;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[3]};
  padding-left: ${({ $depth }) => `${12 + Math.min($depth, 6) * 20}px`};
  background-color: ${({ $active }) =>
    $active ? rawColors.primary[50] : rawColors.structural.white};
  color: ${rawColors.text.highEmphasis};
  ${typographyMixin("body", 3)}

  ${({ $depth, $isLast }) =>
    $depth > 0
      ? `
    &::before {
      content: "";
      position: absolute;
      left: ${12 + ($depth - 1) * 20 + 6}px;
      top: 0;
      bottom: ${$isLast ? "50%" : "0"};
      width: 1px;
      background: ${rawColors.stroke[300]};
    }

    &::after {
      content: "";
      position: absolute;
      left: ${12 + ($depth - 1) * 20 + 6}px;
      top: 50%;
      width: 14px;
      height: 1px;
      background: ${rawColors.stroke[300]};
    }
  `
      : ""}
`;

const Expander = styled.button`
  width: 14px;
  height: 14px;
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;
`;

const NodeLabel = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingWrap = styled.div`
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const normalizeErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) return error.message;
  return "Failed to load connector assets. Please retry.";
};

const inFlightAssetListRequests = new Map<
  string,
  Promise<ConnectorAssetResponsePayload[]>
>();

const buildAssetListRequestKey = (
  catalogKey: string,
  maxDepth: number,
  pageLimit: number,
): string => `${catalogKey}::${maxDepth}::${pageLimit}`;

const listAssetsOnce = async (
  catalogKey: string,
  maxDepth: number,
  pageLimit: number,
): Promise<ConnectorAssetResponsePayload[]> => {
  const requestKey = buildAssetListRequestKey(catalogKey, maxDepth, pageLimit);
  const existingRequest = inFlightAssetListRequests.get(requestKey);
  if (existingRequest) return existingRequest;

  const request = connectorAssetsApi
    .listAll(catalogKey, {
      view: "tree",
      max_depth: maxDepth,
      limit: pageLimit,
    })
    .then((response) => response.items);

  inFlightAssetListRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    inFlightAssetListRequests.delete(requestKey);
  }
};

const flattenLeafKeys = (node: BiDatasetTreeNode): string[] => {
  if (node.children.length === 0) return [node.key];
  return node.children.flatMap(flattenLeafKeys);
};

const filterTree = (
  nodes: BiDatasetTreeNode[],
  query: string,
): BiDatasetTreeNode[] => {
  if (!query) return nodes;
  const lowered = query.toLowerCase();
  const visit = (node: BiDatasetTreeNode): BiDatasetTreeNode | null => {
    const matchedSelf = node.label.toLowerCase().includes(lowered);
    const matchedChildren = node.children
      .map(visit)
      .filter((child): child is BiDatasetTreeNode => child !== null);
    if (matchedSelf || matchedChildren.length > 0) {
      return { ...node, children: matchedChildren };
    }
    return null;
  };
  return nodes
    .map(visit)
    .filter((node): node is BiDatasetTreeNode => node !== null);
};

export const BiSelectStep: React.FC<StepRenderProps> = ({
  step,
  values,
  wizardConfig,
  catalogKey,
  setValue,
  setApiError,
}) => {
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const [allAssets, setAllAssets] = useState<
    ConnectorAssetResponsePayload[] | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const selectedLeafKeys = useMemo(
    () =>
      new Set(
        Array.isArray(values.selectedTables)
          ? values.selectedTables.filter(
              (item): item is string => typeof item === "string",
            )
          : [],
      ),
    [values.selectedTables],
  );

  const selectInfoMessage = useMemo(() => {
    const treeHints = wizardConfig.behavior?.treeHints;
    if (
      treeHints &&
      typeof treeHints === "object" &&
      typeof treeHints.select_info_message === "string" &&
      treeHints.select_info_message.trim().length > 0
    ) {
      return treeHints.select_info_message.trim();
    }
    if (typeof step.note === "string" && step.note.trim().length > 0) {
      return step.note.trim();
    }
    return "You can choose specific dashboards and reports, or skip this step to import everything.";
  }, [step.note, wizardConfig.behavior?.treeHints]);

  useEffect(() => {
    let cancelled = false;
    setAssetsLoading(true);
    setAssetsError(null);
    setApiError(null);
    void (async () => {
      try {
        const maxDepth = wizardConfig.behavior?.assetSyncMaxDepth ?? 3;
        const pageLimit = wizardConfig.behavior?.assetSyncPageLimit ?? 500;
        const items = await listAssetsOnce(catalogKey, maxDepth, pageLimit);
        if (cancelled) return;
        setAllAssets(items);
      } finally {
        if (!cancelled) setAssetsLoading(false);
      }
    })().catch((error: unknown) => {
      if (cancelled) return;
      const message = normalizeErrorMessage(error);
      setAssetsError(message);
      setApiError(message);
    });
    return () => {
      cancelled = true;
    };
  }, [
    catalogKey,
    setApiError,
    wizardConfig.behavior?.assetSyncMaxDepth,
    wizardConfig.behavior?.assetSyncPageLimit,
  ]);

  const { nodes, tableMetaByKey } = useMemo(() => {
    if (!allAssets) return { nodes: [], tableMetaByKey: {} };
    return mapBiAssetsFromTreeResponse(allAssets);
  }, [allAssets]);

  useEffect(() => {
    setValue("tablesByKey", tableMetaByKey);
  }, [setValue, tableMetaByKey]);

  useEffect(() => {
    setValue("biTreeNodes", nodes);
  }, [nodes, setValue]);

  useEffect(() => {
    if (nodes.length === 0) return;
    setExpandedIds((prev) => {
      if (prev.size > 0) return prev;
      return new Set(nodes.map((node) => node.id));
    });
  }, [nodes]);

  const filteredNodes = useMemo(
    () => filterTree(nodes, searchQuery.trim()),
    [nodes, searchQuery],
  );

  const toggleNodeSelection = (
    node: BiDatasetTreeNode,
    nextChecked: boolean,
  ) => {
    const keys = flattenLeafKeys(node);
    const next = new Set(selectedLeafKeys);
    if (nextChecked) {
      for (const key of keys) next.add(key);
    } else {
      for (const key of keys) next.delete(key);
    }
    setValue("selectedTables", Array.from(next));
  };

  const renderNode = (
    node: BiDatasetTreeNode,
    depth: number,
    isLast: boolean,
  ): React.ReactNode => {
    const descendantLeafKeys = flattenLeafKeys(node);
    const selectedCount = descendantLeafKeys.filter((key) =>
      selectedLeafKeys.has(key),
    ).length;
    const isChecked =
      descendantLeafKeys.length > 0 &&
      selectedCount === descendantLeafKeys.length;
    const isIndeterminate =
      selectedCount > 0 && selectedCount < descendantLeafKeys.length;
    const isExpanded = expandedIds.has(node.id);
    const isParent = node.children.length > 0;
    const isActive = isChecked || isIndeterminate;

    return (
      <React.Fragment key={node.id}>
        <Row $depth={depth} $active={isActive} $isLast={isLast}>
          {isParent ? (
            <Expander
              type="button"
              aria-label={
                isExpanded ? `Collapse ${node.label}` : `Expand ${node.label}`
              }
              onClick={() =>
                setExpandedIds((prev) => {
                  const next = new Set(prev);
                  if (next.has(node.id)) next.delete(node.id);
                  else next.add(node.id);
                  return next;
                })
              }
            >
              {isExpanded ? (
                <Minus size={12} strokeWidth={2} />
              ) : (
                <Plus size={12} strokeWidth={2} />
              )}
            </Expander>
          ) : (
            <span />
          )}
          <Checkbox
            checked={isChecked}
            indeterminate={isIndeterminate}
            onChange={(next) => toggleNodeSelection(node, next)}
            ariaLabel={`Select ${node.label}`}
          />
          {isParent ? (
            <Folder size={14} color={rawColors.text.mediumEmphasis} />
          ) : (
            <FileText size={14} color={rawColors.text.mediumEmphasis} />
          )}
          <NodeLabel>{node.label}</NodeLabel>
        </Row>
        {isParent && isExpanded
          ? node.children.map((child, childIndex) =>
              renderNode(
                child,
                depth + 1,
                childIndex === node.children.length - 1,
              ),
            )
          : null}
      </React.Fragment>
    );
  };

  if (assetsLoading && !allAssets) {
    return (
      <Wrap>
        <LoadingWrap>
          <EmptyState
            icon={<Spinner size={28} aria-hidden />}
            caption="Loading dataset tree"
          />
        </LoadingWrap>
      </Wrap>
    );
  }

  if (assetsError) {
    return (
      <Wrap>
        <LoadingWrap>
          <EmptyState
            icon={<FileText size={24} aria-hidden />}
            caption={assetsError}
          />
        </LoadingWrap>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <HeaderRow>
        <Title>Dataset</Title>
        <SearchWrap>
          <Search size={16} />
          <SearchInput
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search catalog"
            aria-label="Search catalog"
          />
        </SearchWrap>
      </HeaderRow>
      <InfoBanner>{selectInfoMessage}</InfoBanner>
      <TreeCard>
        <TreeScroll>
          {filteredNodes.length === 0 ? (
            <EmptyState
              icon={<FileText size={24} aria-hidden />}
              caption="No matching datasets found."
            />
          ) : (
            filteredNodes.map((node, nodeIndex) =>
              renderNode(node, 0, nodeIndex === filteredNodes.length - 1),
            )
          )}
        </TreeScroll>
      </TreeCard>
    </Wrap>
  );
};

export default BiSelectStep;
